// components/entities/pages/OrganisationTeamsTab.tsx
import React, { useCallback, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { CircleDollarSign, CreditCard, Landmark, Minus, MoveHorizontal, Plus, Send, Trash2, UserCheck, Users } from '@/components/ui/lucide-memo';
import Select from '@/components/ui/Select';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { InfiniteViewport } from '@/components/viewport/InfiniteViewport';
import clsx from 'clsx';
import tableStyles from '@/components/portal/Table.module.css';
import { TABLE_GLASS_HEADER, TABLE_GLASS_ROW } from '@/components/portal/tableSurface';
import { HeadlineStats } from '@/components/portal/HeadlineStats';
import { portalCard, portalFilterButton, portalInput, portalPill, portalSectionTitle } from '@/components/portal/portalTheme';
import UserAvatar from '@/components/UserAvatar';
import { EntityView } from '../EntityView';
import { OrganisationPoolCircle, TeamPoolCircle } from './organisation-teams/PoolCircles';
import {
  DEFAULT_TEAMS_VIEWPORT,
  UNASSIGNED_TEAM_VALUE,
  buildTeamsCanvasLayout,
  getMemberDisplayName,
  hydrateOrganisationTeamsInventory,
  poolConnectorPath,
  sortMembers,
  selectOrganisationTeamsRuntime,
  type TeamPoolNode,
  type TeamsCanvasLayout,
} from './organisation-teams/model';
import { formatCreditsInteger } from '#lib/formatCredits';
import { getSpecificErrorMessage } from '#lib/utils';
import { isAuthorizedForOrganisation } from '#lib/organisation-teams/team-access';
import {
  canAllocateFromOrg as canAllocateFromOrgRule,
  canCreateTeam as canCreateTeamRule,
  canReleaseToOrg as canReleaseToOrgRule,
  canTransferCredits as canTransferCreditsRule,
  checkTeamBudgetDelta,
  getNextReservedCredits,
  getOrganisationPlanAvailableCredits,
  isEligibleTransferTarget,
  mergeOrganisationTeams,
  readTeamCreditPool,
} from '#lib/organisation-teams/team-rules';
import type {
  LeasingUserDTO,
  OrganisationInventoryEntityState,
  OrganisationPlanDTO,
  OrganisationRuntimeState,
  OrganisationTeamDTO,
  OrganisationTeamsCanvasRuntimeState,
} from '#types/types';

/** Small figure tile used by the right panel. Inlined when the pool canvas was removed. */
const CreditMetric = ({ label, value }: { label: string; value: number }) => (
  <div className="rounded-lg border border-white/10 bg-white/[0.035] px-2 py-1.5">
    <p className="text-[11px] font-medium text-white/40">{label}</p>
    <p className="mt-1 font-mono text-xs text-white/78">{formatCreditsInteger(value)}</p>
  </div>
);

interface OrganisationTeamsTabProps {
  organisationId: string;
  renderLegacy2D?: boolean;
}

interface OrganisationTeamsModel {
  organisation: OrganisationInventoryEntityState | null;
  members: LeasingUserDTO[];
  teams: OrganisationTeamDTO[];
  layout: TeamsCanvasLayout;
  teamsRuntime: OrganisationTeamsCanvasRuntimeState | null;
  selectedTeamId: string | null;
  selectedMemberId: string | null;
  selectedSourcePlanId: string | null;
  selectedTeam: TeamPoolNode | null;
  selectedMember: LeasingUserDTO | null;
  selectedMemberTeamId: string | null;
  selectedMemberCurrentTeam: TeamPoolNode | null;
  selectedTeamSourcePlan: OrganisationPlanDTO | null;
  selectedTeamSourcePlanAvailable: number;
  pendingAction: OrganisationTeamsCanvasRuntimeState['pendingAction'] | null;
  memberMoveTargetValue: string;
  transferTargetTeamId: string;
  budgetCreditsInput: string;
  transferCreditsInput: string;
  teamLifecycleMode: OrganisationTeamsCanvasRuntimeState['teamLifecycleMode'] | null;
  createTeamNameInput: string;
  createTeamDescriptionInput: string;
  createTeamSourcePlanId: string;
  createTeamCreditsInput: string;
  createTeamSourcePlan: OrganisationPlanDTO | null;
  createTeamSourcePlanAvailable: number;
  budgetDraftMode: OrganisationTeamsCanvasRuntimeState['budgetDraftMode'] | null;
  eligibleTransferTargets: TeamPoolNode[];
  transferAmount: number;
  budgetAmount: number;
  createTeamAmount: number;
  canTransferCredits: boolean;
  canAllocateFromOrg: boolean;
  canReleaseToOrg: boolean;
  canCreateTeam: boolean;
  canManageTeams: boolean;
  patchTeamsRuntime: (patch: Partial<OrganisationTeamsCanvasRuntimeState>) => void;
  refreshTeamsData: () => Promise<void>;
}

const inputClass = `${portalInput} w-full !h-10 !rounded-xl !px-3 !text-xs disabled:cursor-not-allowed disabled:opacity-50`;
const teamSelectStyle: React.CSSProperties = {
  width: '100%',
  height: '2.5rem',
  borderRadius: '0.75rem',
  backgroundColor: '#1A1A1A',
  border: '1px solid #2A2A2A',
  color: 'rgba(255,255,255,0.82)',
  fontSize: '12px',
  outline: 'none',
};
const buttonClass = `${portalFilterButton} h-9 w-full justify-center rounded-xl px-3 text-xs disabled:cursor-not-allowed disabled:opacity-45`;
const primaryButtonClass = 'inline-flex h-9 w-full items-center justify-center gap-2 rounded-xl border border-[#3B82F6]/35 bg-[#1E3563] px-3 text-xs font-medium text-white transition hover:bg-[#254173] disabled:cursor-not-allowed disabled:opacity-45';
const panelCardClass = `${portalCard} rounded-xl p-3 shadow-none`;
const panelPillClass = `${portalPill} inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px]`;

const TeamMemberRow = React.memo(({ member, isSelected, onSelect }: {
  member: LeasingUserDTO;
  isSelected: boolean;
  onSelect: (memberId: string) => void;
}) => {
  const handleClick = useCallback(() => onSelect(member.id), [member.id, onSelect]);
  return (
    <button
      type="button"
      className={`flex w-full min-w-0 items-center gap-2 rounded-xl border px-2 py-1.5 text-left text-xs transition ${
        isSelected ? 'border-[#3B82F6]/35 bg-[#1E3563]/45 text-white' : 'border-transparent text-white/70 hover:border-white/10 hover:bg-white/[0.045] hover:text-white/90'
      }`}
      onClick={handleClick}
    >
      <UserAvatar
        userId={member.id}
        name={getMemberDisplayName(member)}
        canEdit={false}
        isOrganisationAdmin={false}
        size={24}
        shape="circle"
        className="!bg-transparent"
      />
      <span className="truncate">{getMemberDisplayName(member)}</span>
    </button>
  );
});
TeamMemberRow.displayName = 'TeamMemberRow';

const formatPlanLabel = (plan: OrganisationPlanDTO | null | undefined): string => {
  if (!plan) return 'Unknown source plan';
  return plan.priceDisplayText || `Plan ${plan.id.slice(0, 8)}`;
};

const useOrganisationTeamsModel = (organisationId: string): OrganisationTeamsModel => {
  const { kernel } = useKernel();
  const organisation = useKernelStore(
    (state) => (state.inventory.Organisation.registry[organisationId] as OrganisationInventoryEntityState | undefined) ?? null,
  );
  const userRegistry = useKernelStore((state) => state.inventory.User.registry);
  const teamRegistry = useKernelStore((state) => state.inventory.Team.registry);
  const teamsRuntime = useKernelStore((state) => selectOrganisationTeamsRuntime(organisationId, state.inventory.Organisation.registry, state.entityKernelState));

  const members = useMemo(() => Object.values(userRegistry)
    .filter((user): user is LeasingUserDTO => Boolean(user && user.organisationId === organisationId))
    .sort(sortMembers), [organisationId, userRegistry]);
  const hasOrganisationTeamsSnapshot = Array.isArray(organisation?.teams);
  const organisationTeamsSnapshot = useMemo(
    () => (hasOrganisationTeamsSnapshot ? organisation?.teams ?? [] : []),
    [hasOrganisationTeamsSnapshot, organisation?.teams],
  );
  const inventoryTeams = useMemo(() => Object.values(teamRegistry)
    .filter((team): team is OrganisationTeamDTO => Boolean(
      team &&
      team.organisationId === organisationId,
    ))
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt)), [organisationId, teamRegistry]);
  const teams = useMemo(
    () => mergeOrganisationTeams(inventoryTeams, hasOrganisationTeamsSnapshot ? organisationTeamsSnapshot : null),
    [hasOrganisationTeamsSnapshot, inventoryTeams, organisationTeamsSnapshot],
  );
  const layout = useMemo(() => buildTeamsCanvasLayout(organisation, members, teams), [organisation, members, teams]);

  const selectedTeamId = teamsRuntime?.selectedTeamId ?? null;
  const selectedMemberId = teamsRuntime?.selectedMemberId ?? null;
  const selectedSourcePlanId = teamsRuntime?.selectedSourcePlanId ?? null;
  const pendingAction = teamsRuntime?.pendingAction ?? null;
  const selectedTeam = useMemo(
    () => layout.teams.find((node) => node.team.id === selectedTeamId) ?? null,
    [layout.teams, selectedTeamId],
  );
  const selectedMember = useMemo(
    () => members.find((member) => member.id === selectedMemberId) ?? null,
    [members, selectedMemberId],
  );
  const selectedMemberTeamId = useMemo(() => {
    if (!selectedMember) return null;
    if (selectedMember.teamId) return selectedMember.teamId;
    return layout.teams.find((teamNode) => teamNode.team.memberIds.includes(selectedMember.id))?.team.id ?? null;
  }, [layout.teams, selectedMember]);
  const selectedMemberCurrentTeam = useMemo(
    () => layout.teams.find((teamNode) => teamNode.team.id === selectedMemberTeamId) ?? null,
    [layout.teams, selectedMemberTeamId],
  );
  const selectedTeamSourcePlan = useMemo(
    () => selectedTeam?.sourcePlanId
      ? organisation?.plans?.find((plan) => plan.id === selectedTeam.sourcePlanId) ?? null
      : null,
    [organisation?.plans, selectedTeam?.sourcePlanId],
  );
  const selectedTeamSourcePlanAvailable = selectedTeam?.sourcePlanId
    ? getOrganisationPlanAvailableCredits(selectedTeamSourcePlan)
    : layout.organisationPool.available;

  const hasExplicitMoveTarget = Object.prototype.hasOwnProperty.call(teamsRuntime ?? {}, 'memberMoveTargetTeamId');
  const memberMoveTargetTeamId = hasExplicitMoveTarget ? (teamsRuntime?.memberMoveTargetTeamId ?? null) : selectedMemberTeamId;
  const memberMoveTargetValue = memberMoveTargetTeamId || UNASSIGNED_TEAM_VALUE;
  const transferTargetTeamId = teamsRuntime?.transferTargetTeamId ?? '';
  const transferCreditsInput = teamsRuntime?.transferCreditsInput ?? '';
  const budgetCreditsInput = teamsRuntime?.budgetCreditsInput ?? '';
  const teamLifecycleMode = teamsRuntime?.teamLifecycleMode ?? null;
  const createTeamNameInput = teamsRuntime?.createTeamNameInput ?? '';
  const createTeamDescriptionInput = teamsRuntime?.createTeamDescriptionInput ?? '';
  const createTeamSourcePlanId = teamsRuntime?.createTeamSourcePlanId ?? '';
  const createTeamCreditsInput = teamsRuntime?.createTeamCreditsInput ?? '';
  const budgetDraftMode = teamsRuntime?.budgetDraftMode ?? null;

  const createTeamSourcePlan = useMemo(
    () => organisation?.plans?.find((plan) => plan.id === createTeamSourcePlanId) ?? null,
    [createTeamSourcePlanId, organisation?.plans],
  );
  const createTeamSourcePlanAvailable = getOrganisationPlanAvailableCredits(createTeamSourcePlan);

  const selectedTeamPool = useMemo(() => readTeamCreditPool(selectedTeam?.team), [selectedTeam?.team]);
  const eligibleTransferTargets = useMemo(() => {
    if (!selectedTeam) return [];
    const from = { teamId: selectedTeam.team.id, sourcePlanId: selectedTeam.sourcePlanId };
    return layout.teams.filter((teamNode) => isEligibleTransferTarget(from, { teamId: teamNode.team.id, sourcePlanId: teamNode.sourcePlanId }));
  }, [layout.teams, selectedTeam]);

  const transferAmount = Number.parseFloat(transferCreditsInput);
  const budgetAmount = Number.parseFloat(budgetCreditsInput);
  const createTeamAmount = Number.parseFloat(createTeamCreditsInput);
  const canManageTeams = useKernelStore((state) => isAuthorizedForOrganisation({
    isFaterAdmin: state.userContext.isFaterAdmin,
    isOrganisationAdmin: state.userContext.isOrganisationAdmin,
    userOrgId: state.userContext.organisationId,
  }, organisationId));
  const transferTargetTeam = useMemo(
    () => layout.teams.find((teamNode) => teamNode.team.id === transferTargetTeamId) ?? null,
    [layout.teams, transferTargetTeamId],
  );
  const canTransferCredits = Boolean(
    canManageTeams &&
    selectedTeam &&
    pendingAction !== 'TRANSFER_CREDITS' &&
    canTransferCreditsRule({
      fromTeamId: selectedTeam.team.id,
      fromPool: selectedTeamPool,
      to: transferTargetTeam ? { teamId: transferTargetTeam.team.id, sourcePlanId: transferTargetTeam.sourcePlanId } : null,
      amount: transferAmount,
    }),
  );
  const canAllocateFromOrg = Boolean(
    canManageTeams &&
    pendingAction !== 'UPDATE_TEAM_PLAN' &&
    canAllocateFromOrgRule({ pool: selectedTeamPool, amount: budgetAmount, sourcePlanAvailable: selectedTeamSourcePlanAvailable }),
  );
  const canReleaseToOrg = Boolean(
    canManageTeams &&
    pendingAction !== 'UPDATE_TEAM_PLAN' &&
    canReleaseToOrgRule({ pool: selectedTeamPool, amount: budgetAmount }),
  );
  const canCreateTeam = Boolean(
    canManageTeams &&
    pendingAction !== 'CREATE_TEAM' &&
    canCreateTeamRule({
      name: createTeamNameInput,
      sourcePlanId: createTeamSourcePlanId,
      credits: createTeamAmount,
      sourcePlanAvailable: createTeamSourcePlanAvailable,
    }),
  );

  const patchTeamsRuntime = useCallback((patch: Partial<OrganisationTeamsCanvasRuntimeState>) => {
    if (!kernel.current) return;
    const runtime = kernel.current.readEntityRuntimeState('Organisation', organisationId) as Partial<OrganisationRuntimeState>;
    kernel.current.patchEntityRuntimeState('Organisation', organisationId, {
      teamsCanvas: {
        ...(runtime.teamsCanvas ?? {}),
        ...patch,
      },
    });
  }, [kernel, organisationId]);

  const refreshTeamsData = useCallback(async () => {
    if (!kernel.current) return;
    await Promise.all([
      kernel.current.syncEntity('Organisation', organisationId, { force: true }),
      hydrateOrganisationTeamsInventory(kernel, organisationId, { force: true }),
    ]);
  }, [kernel, organisationId]);

  return {
    organisation,
    members,
    teams,
    layout,
    teamsRuntime,
    selectedTeamId,
    selectedMemberId,
    selectedSourcePlanId,
    selectedTeam,
    selectedMember,
    selectedMemberTeamId,
    selectedMemberCurrentTeam,
    selectedTeamSourcePlan,
    selectedTeamSourcePlanAvailable,
    pendingAction,
    memberMoveTargetValue,
    transferTargetTeamId,
    budgetCreditsInput,
    transferCreditsInput,
    teamLifecycleMode,
    createTeamNameInput,
    createTeamDescriptionInput,
    createTeamSourcePlanId,
    createTeamCreditsInput,
    createTeamSourcePlan,
    createTeamSourcePlanAvailable,
    budgetDraftMode,
    eligibleTransferTargets,
    transferAmount,
    budgetAmount,
    createTeamAmount,
    canTransferCredits,
    canAllocateFromOrg,
    canReleaseToOrg,
    canCreateTeam,
    canManageTeams,
    patchTeamsRuntime,
    refreshTeamsData,
  };
};

const POOL_COLUMNS = [
  { id: 'pool', header: 'Budget pool', width: 260 },
  { id: 'members', header: 'Members', width: 100, align: 'right' as const },
  { id: 'plan', header: 'Source plan', width: 180 },
  { id: 'reserved', header: 'Reserved', width: 120, align: 'right' as const },
  { id: 'used', header: 'Used', width: 120, align: 'right' as const },
  { id: 'remaining', header: 'Remaining', width: 120, align: 'right' as const },
];

const MEMBER_COLUMNS = [
  { id: 'member', header: 'Member', width: 280 },
  { id: 'email', header: 'Email', width: 260 },
  { id: 'team', header: 'Budget pool', width: 220 },
];

const gridVars = (columns: { width: number }[]) =>
  ({
    '--table-grid-cols': columns.map((column) => `${column.width}fr`).join(' '),
    '--table-min-width': `${columns.reduce((sum, column) => sum + column.width, 0)}px`,
  }) as React.CSSProperties;

const TableHead = ({ columns }: { columns: typeof POOL_COLUMNS | typeof MEMBER_COLUMNS }) => (
  <div className={clsx(tableStyles.gridRow, tableStyles.headerRow, TABLE_GLASS_HEADER)}>
    {columns.map((column) => (
      <div
        key={column.id}
        className={clsx(tableStyles.cell, 'align' in column && column.align === 'right' && 'justify-end')}
      >
        <span className="truncate">{column.header}</span>
      </div>
    ))}
  </div>
);

export function OrganisationTeamsTab({ organisationId, renderLegacy2D = false }: OrganisationTeamsTabProps) {
  const { kernel } = useKernel();
  const model = useOrganisationTeamsModel(organisationId);
  const {
    organisation,
    layout,
    teams,
    teamsRuntime,
    selectedTeamId,
    selectedMemberId,
    selectedSourcePlanId,
    pendingAction,
    patchTeamsRuntime,
    refreshTeamsData,
  } = model;
  const showLegacyTeams2D = renderLegacy2D;

  const viewportKey = useMemo(() => `organisation:${organisationId}:teams`, [organisationId]);
  const viewportOwner = useMemo(() => ({
    entityType: 'Organisation' as const,
    entityId: organisationId,
    runtimePath: ['teamsCanvas', 'viewport'] as const,
  }), [organisationId]);
  const canvasStyle = useMemo(() => ({ width: layout.bounds.w, height: layout.bounds.h }), [layout.bounds.w, layout.bounds.h]);
  const shouldAutoFit = showLegacyTeams2D && Boolean(layout.teams.length > 0 || organisation) && teamsRuntime?.hasAutoFitOnce !== true;

  const openTeamsRightPanel = useCallback(() => {
    kernel.current?.openRightPanel(380);
  }, [kernel]);

  // Selection handlers are unchanged: every credit mutation (allocate, release, transfer, create,
  // dissolve, move member) lives in the right panel, and selecting a row is what opens it.
  const handleSelectOrganisationPool = useCallback(() => {
    patchTeamsRuntime({
      selectedSourcePlanId: selectedSourcePlanId === 'organisation' ? null : 'organisation',
      selectedTeamId: null,
      selectedMemberId: null,
      memberMoveTargetTeamId: null,
      budgetDraftMode: null,
    });
    openTeamsRightPanel();
  }, [openTeamsRightPanel, patchTeamsRuntime, selectedSourcePlanId]);

  const handleSelectTeam = useCallback((teamId: string) => {
    patchTeamsRuntime({
      selectedTeamId: selectedTeamId === teamId ? null : teamId,
      selectedSourcePlanId: null,
      selectedMemberId: null,
      memberMoveTargetTeamId: null,
      budgetDraftMode: null,
    });
    openTeamsRightPanel();
  }, [openTeamsRightPanel, patchTeamsRuntime, selectedTeamId]);

  const handleSelectMember = useCallback((memberId: string) => {
    const nextSelectedMemberId = selectedMemberId === memberId ? null : memberId;
    if (!nextSelectedMemberId) {
      patchTeamsRuntime({ selectedMemberId: null, memberMoveTargetTeamId: null });
      return;
    }
    const member = model.members.find((candidate) => candidate.id === nextSelectedMemberId) ?? null;
    const memberTeamId =
      member?.teamId ??
      layout.teams.find((teamNode) => teamNode.team.memberIds.includes(nextSelectedMemberId))?.team.id ??
      null;
    patchTeamsRuntime({
      selectedMemberId: nextSelectedMemberId,
      selectedTeamId: memberTeamId,
      selectedSourcePlanId: null,
      memberMoveTargetTeamId: memberTeamId,
      budgetDraftMode: null,
    });
    openTeamsRightPanel();
  }, [kernel, openTeamsRightPanel, organisationId, patchTeamsRuntime, selectedMemberId]);

  const handleMoveMember = useCallback(async (memberId: string, targetTeamId: string | null) => {
    if (!kernel.current || pendingAction === 'MOVE_MEMBER') return;
    if (!kernel.current.canManageTeams(organisationId)) return;
    const member = kernel.current.getOrganisationMembers(organisationId).find((candidate) => candidate.id === memberId) ?? null;
    if (!member) return;
    const currentTeamId = member.teamId ?? kernel.current.getOrganisationTeams(organisationId).find((team) => team.memberIds.includes(memberId))?.id ?? null;
    if ((targetTeamId ?? null) === (currentTeamId ?? null)) return;

    patchTeamsRuntime({ pendingAction: 'MOVE_MEMBER', selectedMemberId: memberId, selectedTeamId: targetTeamId, memberMoveTargetTeamId: targetTeamId });
    openTeamsRightPanel();
    try {
      await kernel.current.moveOrganisationTeamMembers({ organisationId, memberIds: [memberId], targetTeamId });
      await refreshTeamsData();
      toast.success(targetTeamId ? 'Member moved to team.' : 'Member moved to organisation pool.');
    } catch (error) {
      toast.error(getSpecificErrorMessage(error, 'Failed to move member'));
    } finally {
      patchTeamsRuntime({ pendingAction: null });
    }
  }, [kernel, openTeamsRightPanel, organisationId, patchTeamsRuntime, pendingAction, refreshTeamsData]);

  const handleCreditDrop = useCallback((sourceTeamId: string | null, targetTeamId: string | null) => {
    if (!sourceTeamId && targetTeamId) {
      patchTeamsRuntime({ selectedTeamId: targetTeamId, selectedSourcePlanId: null, selectedMemberId: null, budgetDraftMode: 'ALLOCATE_FROM_ORG', budgetCreditsInput: '' });
      openTeamsRightPanel();
      return;
    }
    if (sourceTeamId && !targetTeamId) {
      patchTeamsRuntime({ selectedTeamId: sourceTeamId, selectedSourcePlanId: null, selectedMemberId: null, budgetDraftMode: 'RELEASE_TO_ORG', budgetCreditsInput: '' });
      openTeamsRightPanel();
      return;
    }
    if (sourceTeamId && targetTeamId && sourceTeamId !== targetTeamId) {
      patchTeamsRuntime({ selectedTeamId: sourceTeamId, selectedSourcePlanId: null, selectedMemberId: null, transferTargetTeamId: targetTeamId, budgetDraftMode: 'TRANSFER_TEAM_TO_TEAM', transferCreditsInput: '' });
      openTeamsRightPanel();
    }
  }, [openTeamsRightPanel, patchTeamsRuntime]);

  useEffect(() => {
    if (!kernel.current || !organisationId) return;
    void hydrateOrganisationTeamsInventory(kernel, organisationId);
  }, [kernel, organisationId]);

  useEffect(() => {
    if (!shouldAutoFit || !kernel.current) return;
    const frameId = requestAnimationFrame(() => {
      kernel.current?.fitKernelViewportToRegisteredBounds(viewportKey, 0);
      const runtime = kernel.current?.readEntityRuntimeState('Organisation', organisationId) as Partial<OrganisationRuntimeState> | undefined;
      kernel.current?.patchEntityRuntimeState('Organisation', organisationId, {
        teamsCanvas: {
          ...(runtime?.teamsCanvas ?? {}),
          hasAutoFitOnce: true,
        },
      });
    });
    return () => cancelAnimationFrame(frameId);
  }, [kernel, organisationId, shouldAutoFit, viewportKey, layout.bounds.w, layout.bounds.h]);

  const teamNameById = useMemo(
    () => new Map(layout.teams.map((teamNode) => [teamNode.team.id, teamNode.team.name])),
    [layout.teams]
  );

  const memberRows = useMemo(() => {
    return [...model.members].sort(sortMembers).map((member) => {
      const teamId =
        member.teamId ??
        layout.teams.find((teamNode) => teamNode.team.memberIds.includes(member.id))?.team.id ??
        null;
      return { member, teamId, teamName: teamId ? teamNameById.get(teamId) ?? null : null };
    });
  }, [layout.teams, model.members, teamNameById]);

  const orgPoolSelected = selectedSourcePlanId === 'organisation' && !selectedTeamId;

  if (!organisation) {
    return (
      <div className="flex min-h-[320px] w-full items-center justify-center rounded-xl border border-[#2A2A2A] bg-[#141414] text-sm text-white/55">
        Loading organisation teams...
      </div>
    );
  }

  if (showLegacyTeams2D) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col rounded-xl border border-[#2A2A2A] bg-[#141414] p-4">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div>
            <h3 className="text-sm text-white/70">Credit budget pools</h3>
            <p className="mt-1 text-xs text-white/40">Organisation credits sit above team budget pools. Drag members between orbits; drag credit badges to prepare allocations or transfers in the right panel.</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-white/45">
            <span>{layout.organisationPool.members.length} unassigned</span>
            <span className="h-1 w-1 rounded-full bg-white/20" />
            <span>{layout.teams.length} budget pool{layout.teams.length === 1 ? '' : 's'}</span>
          </div>
        </div>
        <InfiniteViewport
          viewportKey={viewportKey}
          owner={viewportOwner}
          contentBounds={layout.bounds}
          defaultViewport={DEFAULT_TEAMS_VIEWPORT}
          minZoom={0.004}
          maxZoom={4096}
          fitMargin={1.06}
          dragPanMode="anywhere"
          className="min-h-0 flex-1"
          contentClassName="select-none"
        >
          <div className="relative" style={canvasStyle}>
            <svg className="pointer-events-none absolute inset-0" width={layout.bounds.w} height={layout.bounds.h} viewBox={`0 0 ${layout.bounds.w} ${layout.bounds.h}`} aria-hidden="true">
              {layout.teams.map((teamNode) => (
                <path
                  key={`fusion:${teamNode.team.id}`}
                  d={poolConnectorPath(layout.organisationPool, teamNode)}
                  fill="none"
                  stroke={selectedTeamId === teamNode.team.id ? 'rgba(59,130,246,0.45)' : 'rgba(255,255,255,0.12)'}
                  strokeWidth={selectedTeamId === teamNode.team.id ? 2 : 1.25}
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </svg>
            <OrganisationPoolCircle
              node={layout.organisationPool}
              organisationId={organisationId}
              onSelect={handleSelectOrganisationPool}
              onSelectMember={handleSelectMember}
              onDropMember={handleMoveMember}
              onCreditDrop={handleCreditDrop}
            />
            {layout.teams.map((teamNode) => (
              <TeamPoolCircle
                key={teamNode.team.id}
                node={teamNode}
                organisationId={organisationId}
                onSelect={handleSelectTeam}
                onSelectMember={handleSelectMember}
                onDropMember={handleMoveMember}
                onCreditDrop={handleCreditDrop}
              />
            ))}
          </div>
        </InfiniteViewport>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col rounded-xl border border-[#2A2A2A] bg-[#141414]">
      <div className="flex shrink-0 items-start justify-between gap-4 px-4 pt-4">
        <div>
          <h3 className="text-sm text-white/70">Credit budget pools</h3>
          <p className="mt-1 text-xs text-white/40">
            Organisation credits sit above team budget pools. Select a pool or a member to allocate,
            transfer or move in the right panel.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3 text-xs text-white/45">
          <span>{layout.organisationPool.members.length} unassigned</span>
          <span className="h-1 w-1 rounded-full bg-white/20" />
          <span>
            {layout.teams.length} budget pool{layout.teams.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      <div className="shrink-0 overflow-x-auto border-b border-[#2A2A2A] px-4 py-3 custom-scrollbar">
        <HeadlineStats
          stats={[
            { value: formatCreditsInteger(layout.organisationPool.total), label: 'Total' },
            { value: formatCreditsInteger(layout.organisationPool.available), label: 'Free' },
            { value: formatCreditsInteger(layout.organisationPool.used), label: 'Used' },
            { value: formatCreditsInteger(layout.organisationPool.reserved), label: 'Reserved' },
            { value: String(teams.length), label: 'Teams' },
          ]}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto custom-scrollbar">
        <div
          className={clsx(tableStyles.tableContainer, tableStyles.glassSurface, '!border-transparent !bg-transparent')}
          style={gridVars(POOL_COLUMNS)}
        >
          <TableHead columns={POOL_COLUMNS} />

          {/* The organisation pool is just the first row: same selection, same right panel. */}
          <div
            className={clsx(tableStyles.gridRow, tableStyles.bodyRow, TABLE_GLASS_ROW, orgPoolSelected && '!bg-white/[0.06]')}
            style={{ minWidth: 'var(--table-min-width, 0)' }}
            onClick={handleSelectOrganisationPool}
          >
            <div className={tableStyles.cell}>
              <span className="truncate text-[13px] text-white/85">{layout.organisationPool.label}</span>
            </div>
            <div className={clsx(tableStyles.cell, 'justify-end')}>
              <span className="font-mono text-[12px] text-white/60">{layout.organisationPool.members.length}</span>
            </div>
            <div className={tableStyles.cell}>
              <span className="truncate text-[12px] text-white/30">All plans</span>
            </div>
            <div className={clsx(tableStyles.cell, 'justify-end')}>
              <span className="font-mono text-[12px] text-white/60">
                {formatCreditsInteger(layout.organisationPool.reserved)}
              </span>
            </div>
            <div className={clsx(tableStyles.cell, 'justify-end')}>
              <span className="font-mono text-[12px] text-white/60">
                {formatCreditsInteger(layout.organisationPool.used)}
              </span>
            </div>
            <div className={clsx(tableStyles.cell, 'justify-end')}>
              <span className="font-mono text-[12px] text-white/85">
                {formatCreditsInteger(layout.organisationPool.available)}
              </span>
            </div>
          </div>

          {layout.teams.map((teamNode) => {
            const isSelected = selectedTeamId === teamNode.team.id;
            const plan =
              (organisation?.plans ?? []).find((candidate) => candidate.id === teamNode.sourcePlanId) ?? null;
            return (
              <div
                key={teamNode.team.id}
                className={clsx(tableStyles.gridRow, tableStyles.bodyRow, TABLE_GLASS_ROW, isSelected && '!bg-white/[0.06]')}
                style={{ minWidth: 'var(--table-min-width, 0)' }}
                onClick={() => handleSelectTeam(teamNode.team.id)}
              >
                <div className={tableStyles.cell}>
                  <span className="truncate text-[13px] text-white/85" title={teamNode.team.name}>
                    {teamNode.team.name}
                  </span>
                </div>
                <div className={clsx(tableStyles.cell, 'justify-end')}>
                  <span className="font-mono text-[12px] text-white/60">{teamNode.members.length}</span>
                </div>
                <div className={tableStyles.cell}>
                  <span className="truncate text-[12px] text-white/50">
                    {plan ? formatPlanLabel(plan) : <span className="text-white/25">No plan</span>}
                  </span>
                </div>
                <div className={clsx(tableStyles.cell, 'justify-end')}>
                  <span className="font-mono text-[12px] text-white/60">{formatCreditsInteger(teamNode.reserved)}</span>
                </div>
                <div className={clsx(tableStyles.cell, 'justify-end')}>
                  <span className="font-mono text-[12px] text-white/60">{formatCreditsInteger(teamNode.used)}</span>
                </div>
                <div className={clsx(tableStyles.cell, 'justify-end')}>
                  <span className="font-mono text-[12px] text-white/85">{formatCreditsInteger(teamNode.remaining)}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-4 pb-2 pt-6">
          <h4 className="text-xs text-white/40">Members</h4>
        </div>
        <div
          className={clsx(tableStyles.tableContainer, tableStyles.glassSurface, '!border-transparent !bg-transparent')}
          style={gridVars(MEMBER_COLUMNS)}
        >
          <TableHead columns={MEMBER_COLUMNS} />
          {memberRows.map(({ member, teamName }) => {
            const isSelected = selectedMemberId === member.id;
            const name = getMemberDisplayName(member);
            return (
              <div
                key={member.id}
                className={clsx(tableStyles.gridRow, tableStyles.bodyRow, TABLE_GLASS_ROW, isSelected && '!bg-white/[0.06]')}
                style={{ minWidth: 'var(--table-min-width, 0)' }}
                onClick={() => handleSelectMember(member.id)}
              >
                <div className={tableStyles.cell}>
                  <div className="flex min-w-0 items-center gap-2">
                    <UserAvatar userId={member.id} name={name} canEdit={false} size={22} />
                    <span className="truncate text-[13px] text-white/85" title={name}>
                      {name}
                    </span>
                  </div>
                </div>
                <div className={tableStyles.cell}>
                  <span className="truncate text-[12px] text-white/50" title={member.email ?? ''}>
                    {member.email ?? '-'}
                  </span>
                </div>
                <div className={tableStyles.cell}>
                  <span className="truncate text-[12px] text-white/50">
                    {teamName ?? <span className="text-white/25">Unassigned</span>}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function OrganisationTeamsRightPanel({ organisationId }: OrganisationTeamsTabProps) {
  const { kernel } = useKernel();
  const model = useOrganisationTeamsModel(organisationId);
  const {
    organisation,
    layout,
    selectedTeam,
    selectedMember,
    selectedMemberTeamId,
    selectedMemberCurrentTeam,
    selectedTeamSourcePlan,
    selectedTeamSourcePlanAvailable,
    pendingAction,
    memberMoveTargetValue,
    transferTargetTeamId,
    budgetCreditsInput,
    transferCreditsInput,
    teamLifecycleMode,
    createTeamNameInput,
    createTeamDescriptionInput,
    createTeamSourcePlanId,
    createTeamCreditsInput,
    createTeamSourcePlan,
    createTeamSourcePlanAvailable,
    budgetDraftMode,
    eligibleTransferTargets,
    transferAmount,
    budgetAmount,
    createTeamAmount,
    canTransferCredits,
    canAllocateFromOrg,
    canReleaseToOrg,
    canCreateTeam,
    canManageTeams,
    patchTeamsRuntime,
    refreshTeamsData,
  } = model;

  const memberMoveOptions = useMemo(() => [
    { value: UNASSIGNED_TEAM_VALUE, label: 'Organisation pool' },
    ...layout.teams.map((teamNode) => ({ value: teamNode.team.id, label: teamNode.team.name })),
  ], [layout.teams]);

  const sourcePlanOptions = useMemo(() => [
    { value: '', label: 'Source plan', disabled: true },
    ...(organisation?.plans ?? []).map((plan) => {
      const available = getOrganisationPlanAvailableCredits(plan);
      return {
        value: plan.id,
        label: `${formatPlanLabel(plan)} · ${formatCreditsInteger(available)} free`,
        disabled: available <= 0,
      };
    }),
  ], [organisation?.plans]);

  const transferTargetOptions = useMemo(() => [
    { value: '', label: 'Target team', disabled: true },
    ...eligibleTransferTargets.map((teamNode) => ({ value: teamNode.team.id, label: teamNode.team.name })),
  ], [eligibleTransferTargets]);

  const handleMoveTargetChange = useCallback((value: string) => {
    patchTeamsRuntime({ memberMoveTargetTeamId: value === UNASSIGNED_TEAM_VALUE ? null : value });
  }, [patchTeamsRuntime]);

  const handleTransferTargetChange = useCallback((value: string) => {
    patchTeamsRuntime({ transferTargetTeamId: value || null, budgetDraftMode: 'TRANSFER_TEAM_TO_TEAM' });
  }, [patchTeamsRuntime]);

  const handleTransferCreditsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    patchTeamsRuntime({ transferCreditsInput: event.target.value, budgetDraftMode: 'TRANSFER_TEAM_TO_TEAM' });
  }, [patchTeamsRuntime]);

  const handleBudgetCreditsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    patchTeamsRuntime({ budgetCreditsInput: event.target.value });
  }, [patchTeamsRuntime]);

  const selectedTeamIdForMemberMove = selectedTeam?.team.id ?? null;
  const handleSelectTeamMember = useCallback((memberId: string) => {
    patchTeamsRuntime({ selectedMemberId: memberId, memberMoveTargetTeamId: selectedTeamIdForMemberMove });
  }, [patchTeamsRuntime, selectedTeamIdForMemberMove]);

  const openDissolveTeam = useCallback(() => {
    patchTeamsRuntime({ teamLifecycleMode: 'DISSOLVE_TEAM' });
  }, [patchTeamsRuntime]);

  const openCreateTeam = useCallback(() => {
    const fallbackPlan = (organisation?.plans ?? []).find((plan) => getOrganisationPlanAvailableCredits(plan) > 0) ?? null;
    patchTeamsRuntime({
      teamLifecycleMode: 'CREATE_TEAM',
      selectedTeamId: null,
      selectedMemberId: null,
      selectedSourcePlanId: null,
      createTeamSourcePlanId: createTeamSourcePlanId || fallbackPlan?.id || '',
    });
  }, [createTeamSourcePlanId, organisation?.plans, patchTeamsRuntime]);

  const closeLifecycleMode = useCallback(() => {
    patchTeamsRuntime({ teamLifecycleMode: null });
  }, [patchTeamsRuntime]);

  const handleCreateTeamNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    patchTeamsRuntime({ createTeamNameInput: event.target.value, teamLifecycleMode: 'CREATE_TEAM' });
  }, [patchTeamsRuntime]);

  const handleCreateTeamDescriptionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    patchTeamsRuntime({ createTeamDescriptionInput: event.target.value, teamLifecycleMode: 'CREATE_TEAM' });
  }, [patchTeamsRuntime]);

  const handleCreateTeamSourcePlanChange = useCallback((value: string) => {
    patchTeamsRuntime({ createTeamSourcePlanId: value, teamLifecycleMode: 'CREATE_TEAM' });
  }, [patchTeamsRuntime]);

  const handleCreateTeamCreditsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    patchTeamsRuntime({ createTeamCreditsInput: event.target.value, teamLifecycleMode: 'CREATE_TEAM' });
  }, [patchTeamsRuntime]);

  const handleCreateTeam = useCallback(async () => {
    if (!kernel.current || !canCreateTeam || pendingAction) return;
    patchTeamsRuntime({ pendingAction: 'CREATE_TEAM', teamLifecycleMode: 'CREATE_TEAM' });
    try {
      const createdTeam = await kernel.current.createOrganisationTeam({
        organisationId,
        name: createTeamNameInput.trim(),
        description: createTeamDescriptionInput.trim() || null,
        memberIds: [],
        sourceOrganisationPlanId: createTeamSourcePlanId,
        reservedCreditsFloat: createTeamAmount,
        allowOverageToOrg: true,
        autoTopUpSourcePlan: false,
      });
      await refreshTeamsData();
      patchTeamsRuntime({
        selectedTeamId: createdTeam?.id ?? null,
        selectedSourcePlanId: null,
        selectedMemberId: null,
        teamLifecycleMode: null,
        createTeamNameInput: '',
        createTeamDescriptionInput: '',
        createTeamCreditsInput: '',
      });
      toast.success('Team created.');
    } catch (error) {
      toast.error(getSpecificErrorMessage(error, 'Failed to create team'));
    } finally {
      patchTeamsRuntime({ pendingAction: null });
    }
  }, [canCreateTeam, createTeamAmount, createTeamDescriptionInput, createTeamNameInput, createTeamSourcePlanId, kernel, organisationId, patchTeamsRuntime, pendingAction, refreshTeamsData]);

  const handleDissolveTeam = useCallback(async () => {
    if (!selectedTeam || !kernel.current || pendingAction) return;
    if (!kernel.current.canManageTeams(organisationId)) return;
    patchTeamsRuntime({ pendingAction: 'DELETE_TEAM', teamLifecycleMode: 'DISSOLVE_TEAM' });
    try {
      await kernel.current.deleteOrganisationTeam(selectedTeam.team.id);
      await refreshTeamsData();
      patchTeamsRuntime({ selectedTeamId: null, selectedMemberId: null, teamLifecycleMode: null });
      toast.success('Team dissolved. Members returned to the organisation pool.');
    } catch (error) {
      toast.error(getSpecificErrorMessage(error, 'Failed to dissolve team'));
    } finally {
      patchTeamsRuntime({ pendingAction: null });
    }
  }, [kernel, organisationId, patchTeamsRuntime, pendingAction, refreshTeamsData, selectedTeam]);

  const handleMoveSelectedMember = useCallback(async () => {
    if (!selectedMember || !kernel.current || pendingAction) return;
    if (!kernel.current.canManageTeams(organisationId)) return;
    const targetTeamId = memberMoveTargetValue === UNASSIGNED_TEAM_VALUE ? null : memberMoveTargetValue;
    if ((targetTeamId ?? null) === (selectedMemberTeamId ?? null)) return;

    patchTeamsRuntime({ pendingAction: 'MOVE_MEMBER' });
    try {
      await kernel.current.moveOrganisationTeamMembers({
        organisationId,
        memberIds: [selectedMember.id],
        targetTeamId,
      });
      await refreshTeamsData();
      patchTeamsRuntime({ selectedTeamId: targetTeamId, memberMoveTargetTeamId: targetTeamId });
      toast.success(targetTeamId ? 'Member moved to team.' : 'Member moved to organisation pool.');
    } catch (error) {
      toast.error(getSpecificErrorMessage(error, 'Failed to move member'));
    } finally {
      patchTeamsRuntime({ pendingAction: null });
    }
  }, [kernel, memberMoveTargetValue, organisationId, patchTeamsRuntime, pendingAction, refreshTeamsData, selectedMember, selectedMemberTeamId]);

  const handleApplyBudgetDelta = useCallback(async () => {
    if (!selectedTeam || !kernel.current || !selectedTeam.sourcePlanId || pendingAction) return;
    if (!kernel.current.canManageTeams(organisationId)) return;
    const direction = budgetDraftMode === 'ALLOCATE_FROM_ORG' ? 'ALLOCATE_FROM_ORG' : 'RELEASE_TO_ORG';
    const pool = readTeamCreditPool(selectedTeam.team);
    const refusal = checkTeamBudgetDelta({ pool, direction, amount: budgetAmount, sourcePlanAvailable: selectedTeamSourcePlanAvailable });
    if (refusal === 'INVALID_AMOUNT') return;
    if (refusal === 'SOURCE_PLAN_EXHAUSTED') {
      toast.error('This source plan does not have enough free credits.');
      return;
    }
    if (refusal === 'RESERVATION_BELOW_USED') {
      toast.error('A team budget must keep a positive reservation, above the credits it already used.');
      return;
    }
    const nextReserved = getNextReservedCredits(pool, direction, budgetAmount);

    patchTeamsRuntime({ pendingAction: 'UPDATE_TEAM_PLAN', budgetDraftMode: direction });
    try {
      await kernel.current.updateOrganisationTeamPlan(selectedTeam.team.id, {
        sourceOrganisationPlanId: selectedTeam.sourcePlanId,
        reservedCreditsFloat: nextReserved,
        allowOverageToOrg: selectedTeam.team.teamPlan?.allowOverageToOrg ?? true,
        autoTopUpSourcePlan: false,
      });
      await refreshTeamsData();
      patchTeamsRuntime({ budgetCreditsInput: '', budgetDraftMode: null });
      toast.success(direction === 'ALLOCATE_FROM_ORG' ? 'Credits allocated to team.' : 'Credits released to organisation pool.');
    } catch (error) {
      toast.error(getSpecificErrorMessage(error, direction === 'ALLOCATE_FROM_ORG' ? 'Failed to allocate credits' : 'Failed to release credits'));
    } finally {
      patchTeamsRuntime({ pendingAction: null });
    }
  }, [budgetAmount, budgetDraftMode, kernel, organisationId, patchTeamsRuntime, pendingAction, refreshTeamsData, selectedTeam, selectedTeamSourcePlanAvailable]);

  const handleTransferCredits = useCallback(async () => {
    if (!selectedTeam || !kernel.current || !canTransferCredits || pendingAction) return;
    patchTeamsRuntime({ pendingAction: 'TRANSFER_CREDITS', budgetDraftMode: 'TRANSFER_TEAM_TO_TEAM' });
    try {
      await kernel.current.transferOrganisationTeamCredits({
        fromTeamId: selectedTeam.team.id,
        toTeamId: transferTargetTeamId,
        creditsFloat: transferAmount,
      });
      await refreshTeamsData();
      patchTeamsRuntime({ transferCreditsInput: '', transferTargetTeamId: null, budgetDraftMode: null });
      toast.success('Credits transferred between teams.');
    } catch (error) {
      toast.error(getSpecificErrorMessage(error, 'Failed to transfer credits'));
    } finally {
      patchTeamsRuntime({ pendingAction: null });
    }
  }, [canTransferCredits, kernel, patchTeamsRuntime, pendingAction, refreshTeamsData, selectedTeam, transferAmount, transferTargetTeamId]);

  const focusAllocate = useCallback(() => patchTeamsRuntime({ budgetDraftMode: 'ALLOCATE_FROM_ORG' }), [patchTeamsRuntime]);
  const focusRelease = useCallback(() => patchTeamsRuntime({ budgetDraftMode: 'RELEASE_TO_ORG' }), [patchTeamsRuntime]);
  const focusTransfer = useCallback(() => patchTeamsRuntime({ budgetDraftMode: 'TRANSFER_TEAM_TO_TEAM' }), [patchTeamsRuntime]);

  return (
    <div id="portal-right-sidebar-content" className="flex h-full w-full flex-col overflow-hidden bg-black text-white">
      <div className="border-b border-white/10 p-4">
        <div className={`flex items-center gap-2 ${portalSectionTitle}`}>
          <CircleDollarSign size={14} />
          <span>Credits</span>
        </div>
        <p className="mt-2 text-xs text-white/45">Selection and actions for organisation budget pools.</p>
      </div>

      <div className="custom-scrollbar min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        {selectedMember && (
          <div className={panelCardClass}>
            <div className="flex items-start gap-3">
              <UserAvatar
                userId={selectedMember.id}
                name={getMemberDisplayName(selectedMember)}
                canEdit={false}
                isOrganisationAdmin={false}
                size={36}
                shape="circle"
                className="border border-white/10 !bg-transparent"
              />
              <div className="min-w-0">
                <div className={`flex items-center gap-2 ${portalSectionTitle}`}>
                  <UserCheck size={13} />
                  <span>Selected member</span>
                </div>
                <h4 className="mt-2 truncate text-sm font-semibold text-white/88">{getMemberDisplayName(selectedMember)}</h4>
                <p className="truncate text-xs text-white/38">{selectedMember.email}</p>
                <p className="mt-2 text-xs text-white/45">Current: {selectedMemberCurrentTeam?.team.name ?? 'Organisation pool'}</p>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <label className="block text-xs font-medium text-white/45" htmlFor="organisation-team-member-move-target">Move to</label>
              <Select
                id="organisation-team-member-move-target"
                value={memberMoveTargetValue}
                onChange={handleMoveTargetChange}
                options={memberMoveOptions}
                style={teamSelectStyle}
                disabled={Boolean(pendingAction)}
              />
              <button
                type="button"
                className={primaryButtonClass}
                disabled={Boolean(pendingAction) || !canManageTeams || memberMoveTargetValue === (selectedMemberTeamId ?? UNASSIGNED_TEAM_VALUE)}
                onClick={handleMoveSelectedMember}
              >
                <MoveHorizontal size={14} />
                {pendingAction === 'MOVE_MEMBER' ? 'Moving...' : 'Move member'}
              </button>
            </div>
          </div>
        )}

        {selectedTeam && (
          <div className="space-y-4">
            <EntityView type="Team" entityId={selectedTeam.team.id} variant="card" className="!shadow-none" />
            <div className={panelCardClass}>
              <div className="mb-3 flex items-center justify-between text-xs text-white/45">
                <span>Members</span>
                <span className={panelPillClass}>{selectedTeam.members.length}</span>
              </div>
              <div className="space-y-2">
                {selectedTeam.members.length > 0 ? selectedTeam.members.map((member) => (
                  <TeamMemberRow
                    key={member.id}
                    member={member}
                    isSelected={selectedMember?.id === member.id}
                    onSelect={handleSelectTeamMember}
                  />
                )) : <p className="text-xs text-white/35">No members in this team.</p>}
              </div>
            </div>
            <div className={panelCardClass}>
              <div className={`flex items-center gap-2 ${portalSectionTitle}`}>
                <Landmark size={13} />
                <span>Budget pool</span>
              </div>
              <p className="mt-2 text-xs text-white/42">This team spends from its own source plan. Allocations are capped by that plan's free balance.</p>
              <div className="mt-3 space-y-2 rounded-xl border border-white/10 bg-white/[0.025] p-3 text-xs text-white/45">
                <div className="flex items-center justify-between gap-3">
                  <span>Source</span>
                  <span className="truncate text-right text-white/70" title={formatPlanLabel(selectedTeamSourcePlan)}>{formatPlanLabel(selectedTeamSourcePlan)}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Free on source</span>
                  <span className="font-mono text-white/70">{formatCreditsInteger(selectedTeamSourcePlanAvailable)}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Compatible transfers</span>
                  <span className="font-mono text-white/70">{eligibleTransferTargets.length}</span>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <button type="button" className={budgetDraftMode === 'ALLOCATE_FROM_ORG' ? primaryButtonClass : buttonClass} onClick={focusAllocate}>
                  <Plus size={13} /> Add
                </button>
                <button type="button" className={budgetDraftMode === 'RELEASE_TO_ORG' ? primaryButtonClass : buttonClass} onClick={focusRelease}>
                  <Minus size={13} /> Release
                </button>
                <button type="button" className={budgetDraftMode === 'TRANSFER_TEAM_TO_TEAM' ? primaryButtonClass : buttonClass} onClick={focusTransfer}>
                  <Send size={13} /> Send
                </button>
              </div>

              {(budgetDraftMode === 'ALLOCATE_FROM_ORG' || budgetDraftMode === 'RELEASE_TO_ORG') && (
                <div className="mt-3 space-y-2">
                  <input
                    className={inputClass}
                    type="number"
                    min="0"
                    max={budgetDraftMode === 'ALLOCATE_FROM_ORG' ? selectedTeamSourcePlanAvailable : selectedTeam.remaining}
                    step="0.01"
                    value={budgetCreditsInput}
                    onChange={handleBudgetCreditsChange}
                    placeholder={budgetDraftMode === 'ALLOCATE_FROM_ORG'
                      ? `Amount up to ${formatCreditsInteger(selectedTeamSourcePlanAvailable)}`
                      : selectedTeam.used > 0
                        ? `Amount up to ${formatCreditsInteger(selectedTeam.remaining)}`
                        : `Amount below ${formatCreditsInteger(selectedTeam.reserved)}`}
                    disabled={Boolean(pendingAction)}
                  />
                  {budgetDraftMode === 'RELEASE_TO_ORG' && selectedTeam.used <= 0 && (
                    <p className="text-[10px] text-white/32">Release less than the full reservation; team budgets must stay positive.</p>
                  )}
                  <button
                    type="button"
                    className={buttonClass}
                    disabled={Boolean(pendingAction) || (budgetDraftMode === 'ALLOCATE_FROM_ORG' ? !canAllocateFromOrg : !canReleaseToOrg)}
                    onClick={handleApplyBudgetDelta}
                  >
                    {budgetDraftMode === 'ALLOCATE_FROM_ORG' ? <Plus size={14} /> : <Minus size={14} />}
                    {pendingAction === 'UPDATE_TEAM_PLAN'
                      ? 'Updating...'
                      : budgetDraftMode === 'ALLOCATE_FROM_ORG' ? 'Allocate from organisation' : 'Release to organisation'}
                  </button>
                </div>
              )}

              {budgetDraftMode === 'TRANSFER_TEAM_TO_TEAM' && (
                <div className="mt-3 space-y-2">
                  <Select
                    value={transferTargetTeamId}
                    onChange={handleTransferTargetChange}
                    options={transferTargetOptions}
                    style={teamSelectStyle}
                    disabled={Boolean(pendingAction) || eligibleTransferTargets.length === 0}
                  />
                  <input
                    className={inputClass}
                    type="number"
                    min="0"
                    max={selectedTeam.remaining}
                    step="0.01"
                    value={transferCreditsInput}
                    onChange={handleTransferCreditsChange}
                    placeholder={`Amount up to ${formatCreditsInteger(selectedTeam.remaining)}`}
                    disabled={Boolean(pendingAction) || eligibleTransferTargets.length === 0}
                  />
                  <button type="button" className={buttonClass} disabled={Boolean(pendingAction) || !canTransferCredits} onClick={handleTransferCredits}>
                    <Send size={14} />
                    {pendingAction === 'TRANSFER_CREDITS' ? 'Transferring...' : 'Transfer credits'}
                  </button>
                  {eligibleTransferTargets.length === 0 && <p className="text-[10px] text-white/32">No compatible target team for this budget move.</p>}
                </div>
              )}
            </div>
            <div className={panelCardClass}>
              <div className="flex items-center gap-2 text-xs font-medium text-white/50">
                <Trash2 size={13} />
                <span>Team lifecycle</span>
              </div>
              {teamLifecycleMode === 'DISSOLVE_TEAM' ? (
                <div className="mt-3 space-y-3">
                  <p className="text-xs leading-5 text-white/45">
                    Dissolving moves current members back to the organisation pool and releases remaining reserved credits to the source plan. Teams with budget audit history may be blocked by the backend.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" className={buttonClass} onClick={closeLifecycleMode} disabled={Boolean(pendingAction)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-xl border border-[#FF7369]/25 bg-[#FF7369]/10 px-3 text-xs font-medium text-[#FF7369] transition hover:bg-[#FF7369]/15 disabled:cursor-not-allowed disabled:opacity-45"
                      onClick={handleDissolveTeam}
                      disabled={Boolean(pendingAction) || !canManageTeams}
                    >
                      <Trash2 size={14} />
                      {pendingAction === 'DELETE_TEAM' ? 'Dissolving...' : 'Dissolve'}
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className={buttonClass} onClick={openDissolveTeam} disabled={!canManageTeams}>
                  <Trash2 size={14} />
                  Dissolve team
                </button>
              )}
            </div>
          </div>
        )}

        {!selectedTeam && !selectedMember && (
          <div className="space-y-4">
            <div className={panelCardClass}>
              <div className={`flex items-center gap-2 ${portalSectionTitle}`}>
                <CreditCard size={13} />
                <span>Organisation pool</span>
              </div>
              <h4 className="mt-3 truncate text-sm font-semibold text-white/88">{layout.organisationPool.label}</h4>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <CreditMetric label="Total" value={layout.organisationPool.total} />
                <CreditMetric label="Free" value={layout.organisationPool.available} />
                <CreditMetric label="Used" value={layout.organisationPool.used} />
                <CreditMetric label="Teams" value={layout.organisationPool.reserved} />
              </div>
            </div>
            <div className={panelCardClass}>
              <div className="flex items-center gap-2 text-xs font-medium text-white/50">
                <Users size={13} />
                <span>Team lifecycle</span>
              </div>
              {teamLifecycleMode === 'CREATE_TEAM' ? (
                <div className="mt-3 space-y-2">
                  <input
                    className={inputClass}
                    value={createTeamNameInput}
                    onChange={handleCreateTeamNameChange}
                    placeholder="Team name"
                    disabled={Boolean(pendingAction)}
                  />
                  <input
                    className={inputClass}
                    value={createTeamDescriptionInput}
                    onChange={handleCreateTeamDescriptionChange}
                    placeholder="Description"
                    disabled={Boolean(pendingAction)}
                  />
                  <Select
                    value={createTeamSourcePlanId}
                    onChange={handleCreateTeamSourcePlanChange}
                    options={sourcePlanOptions}
                    style={teamSelectStyle}
                    disabled={Boolean(pendingAction)}
                  />
                  <input
                    className={inputClass}
                    type="number"
                    min="0"
                    max={createTeamSourcePlanAvailable}
                    step="0.01"
                    value={createTeamCreditsInput}
                    onChange={handleCreateTeamCreditsChange}
                    placeholder={createTeamSourcePlan ? `Initial budget up to ${formatCreditsInteger(createTeamSourcePlanAvailable)}` : 'Initial budget'}
                    disabled={Boolean(pendingAction) || !createTeamSourcePlanId}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" className={buttonClass} onClick={closeLifecycleMode} disabled={Boolean(pendingAction)}>
                      Cancel
                    </button>
                    <button type="button" className={primaryButtonClass} onClick={handleCreateTeam} disabled={Boolean(pendingAction) || !canCreateTeam}>
                      <Plus size={14} />
                      {pendingAction === 'CREATE_TEAM' ? 'Creating...' : 'Create team'}
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className={primaryButtonClass} onClick={openCreateTeam} disabled={!canManageTeams}>
                  <Plus size={14} />
                  Create team
                </button>
              )}
            </div>
            <div className={`${portalCard} rounded-xl p-4 text-xs text-white/45 shadow-none`}>
              Select a member to move them between budget pools, or select a pool to allocate, release or transfer its credits.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
